/**
 * families — `component_id` → its FAMILY, mirrored from
 * `backend/widget-native/registry.json`.
 *
 * The family is the one thing the registry says about a component that the KIT
 * entry does not: what KIND of thing it is (a dataviz module, a collection, a
 * control, page chrome). Layout decisions that must hold for EVERY component of
 * a kind — the dashboard's elevated panels are the first — read it here instead
 * of listing type names, so a component added to the registry is laid out
 * correctly without a second list being edited to match.
 *
 * It lives in its own module rather than in `registry.js` because `registry.js`
 * imports every component file: a component reading the family FROM registry.js
 * would close the import cycle. Nothing is imported here, so anyone may read it.
 *
 * `registerFamilies` overlays the SERVER registry the same way `registerRegistry`
 * overlays compositions — a family declared there wins, which is what keeps a
 * hot-shipped component laid out by its own declaration rather than by this
 * build's snapshot of the file. A drift test diffs this map against
 * registry.json.
 */

/** The registry's `family` for every component it declares. */
export const FAMILY = {
  // L1 primitives
  Text: 'content',
  Button: 'control',
  Icon: 'content',
  Image: 'media',
  Video: 'media',
  Monogram: 'content',
  Badge: 'content',
  Gauge: 'dataviz',
  Vertical: 'layout',
  Horizontal: 'layout',
  Stack: 'layout',
  Overlay: 'layout',
  Grid: 'layout',
  Surface: 'layout',
  Padding: 'layout',
  Divider: 'layout',
  CaptureButton: 'control',

  // L2 common
  SearchField: 'control',
  OptionSelect: 'control',
  ToolGrid: 'control',
  StatTileRow: 'dataviz',
  MetricRow: 'dataviz',
  Sheet: 'chrome',
  EmptyState: 'status',
  ErrorState: 'status',
  PendingCard: 'collection',
  Card: 'collection',
  NoticeList: 'dataviz',
  MediaPicker: 'media',
  ScanEditor: 'media',
  BarChart: 'dataviz',
  DayBars: 'dataviz',
  TallyCard: 'dataviz',
  MeterCard: 'dataviz',
  DeckCard: 'content',
  ThoughtCard: 'content',
  CountsCard: 'dataviz',
  StatHeadline: 'dataviz',
  DayCells: 'dataviz',
  SectionHeader: 'content',
  KeyValueList: 'content',
  TextBlock: 'content',
  Disclosure: 'content',
  Timeline: 'content',
  // the conversation family: one surface, taken turn by turn
  MessageBubble: 'chat',
  ChatThread: 'chat',
  ChatComposer: 'chat',
  NavList: 'chat',
  CheckList: 'control',
  SelectList: 'control',
  ActionRow: 'control',
  TodoCard: 'control',
  DraftCard: 'control',
  DoneRow: 'status',
  SlotPicker: 'control',
  AgentBubble: 'chat',
  Greeting: 'chrome',
  ToggleRow: 'control',
  InboxHeader: 'chrome',
  InboxRow: 'control',
  InboxText: 'content',
  InboxButton: 'control',
  InboxField: 'control',
  InboxFilter: 'control',
  InboxSteps: 'content',
  ChatPage: 'chat',
  ProposalCard: 'content',
  NotePanel: 'content',
  DeliveryCard: 'content',
  ModeSwitch: 'control',
  TextComposer: 'control',
  NumberField: 'control',
  RoleMediaForm: 'control',
  SlotBoard: 'layout',
  ConnectorLayer: 'dataviz',
  Carousel: 'media',
  BeforeAfterCompare: 'media',
  PageShell: 'chrome',
  ItemCollection: 'collection',
  CategoryShelf: 'collection',
  WishlistShelf: 'collection',
  StatusPanel: 'dataviz',
  ThumbnailShelf: 'collection',
  ActivityShelf: 'collection',
  AnswerShelf: 'collection',
  SessionCollection: 'collection',
  ItemCalendar: 'collection',
  ItemPage: 'chrome',
  SessionPage: 'chrome',
  host: 'host',

  // a2ui core-18 (basic catalog) registered with our kit — families mirror
  // registry.json (a List is layout, a dialog/tab is chrome, an audio clip is
  // media, an input is control).
  List: 'layout',
  Modal: 'chrome',
  Tabs: 'chrome',
  AudioPlayer: 'media',
  CheckBox: 'control',
  ChoicePicker: 'control',
  DateTimeInput: 'control',
  Slider: 'control',
  TextField: 'control',

  // L3 composed
  CaloryRing: 'dataviz',
  WeekReview: 'dataviz',
};

/** Families declared by the installed server registry (hot-update wins). */
let REGISTERED_FAMILY = Object.create(null);

/**
 * The registry's OPTIONAL `skeleton` per component — the loading silhouette a
 * component wants when its family does not predict it. Nothing is bundled here
 * on purpose: the family answers for all 44 components today, and this is the
 * seam a hot-shipped one uses to disagree without a new web bundle.
 */
let REGISTERED_SKELETON = Object.create(null);

/** Install `registry.json`'s families — called by `registerRegistry`. */
export function registerFamilies(registryJson) {
  const next = Object.create(null);
  const skel = Object.create(null);
  for (const c of registryJson?.components || []) {
    if (!c?.id) continue;
    if (c.family) next[c.id] = c.family;
    if (c.skeleton) skel[c.id] = c.skeleton;
  }
  REGISTERED_FAMILY = next;
  REGISTERED_SKELETON = skel;
}

/** Forget the installed families; the bundled map remains. */
export function resetFamilies() {
  REGISTERED_FAMILY = Object.create(null);
  REGISTERED_SKELETON = Object.create(null);
}

/** A type's family, or null when nothing declares one. */
export function familyOf(type) {
  if (!type) return null;
  return REGISTERED_FAMILY[type] || FAMILY[type] || null;
}

/** A type's registry-declared skeleton shape, or null — see `skeletonShapeFor`. */
export function skeletonOf(type) {
  if (!type) return null;
  return REGISTERED_SKELETON[type] || null;
}

/**
 * Components that draw their OWN surface (the shelf treatment — see `ownCard`
 * in primitives.jsx, and DeckCard / ThoughtCard which paint a gradient and an
 * inverted plate of their own). The dashboard shell never panels these: a card
 * that carries its surface, panelled, is a card in a card. Pinned against the
 * registry's `surface: "own"` marker by ownSurface.test.jsx.
 */
export const OWN_SURFACE = new Set([
  'DayCells', 'StatHeadline', 'ProposalCard', 'NotePanel', 'DayBars', 'DeliveryCard',
  'TallyCard', 'MeterCard', 'DeckCard', 'ThoughtCard', 'CountsCard',
]);
